import { useMemo } from 'react';
import { useAppStore } from '../stores/appStore';
import type { FieldConfig } from '../types';

// Default configs as fallback (before API loads)
// Note: 'dependencies' is NOT here — it is a Labs feature, not a FieldConfig
const DEFAULT_CONFIGS: Record<string, Omit<FieldConfig, 'id' | 'createdAt' | 'updatedAt'>> = {
  initiative:     { fieldKey: 'initiative',     label: 'Initiative',     isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 0 },
  title:          { fieldKey: 'title',          label: 'Title',          isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 1 },
  product:        { fieldKey: 'product',        label: 'Product',        isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 2 },
  subProduct:     { fieldKey: 'subProduct',     label: 'Sub-product',    isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 3 },
  status:         { fieldKey: 'status',         label: 'Status',         isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 4 },
  start:          { fieldKey: 'start',          label: 'Start',          isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 5 },
  owner:          { fieldKey: 'owner',          label: 'Owner',          isVisible: true,  isRequired: true,  isLocked: true,  displayOrder: 6 },
  classification: { fieldKey: 'classification', label: 'Classification', isVisible: true,  isRequired: false, isLocked: false, displayOrder: 7 },
  end:            { fieldKey: 'end',            label: 'End',            isVisible: true,  isRequired: false, isLocked: false, displayOrder: 8 },
  tags:           { fieldKey: 'tags',           label: 'Tags',           isVisible: true,  isRequired: false, isLocked: false, displayOrder: 9 },
};

export function useFieldConfig() {
  const fieldConfigs = useAppStore((s) => s.fieldConfigs);

  return useMemo(() => {
    // Build lookup map with defaults as base
    const map = new Map<string, FieldConfig>();

    for (const [key, def] of Object.entries(DEFAULT_CONFIGS)) {
      map.set(key, { id: '', createdAt: '', updatedAt: '', ...def } as FieldConfig);
    }

    // Overlay with actual configs from API
    for (const config of fieldConfigs) {
      map.set(config.fieldKey, config);
    }

    return {
      /** Get config for a specific field */
      getField: (key: string) => map.get(key)!,

      /** Check if a field is visible */
      isVisible: (key: string) => map.get(key)?.isVisible ?? true,

      /** Check if a field is required */
      isRequired: (key: string) => map.get(key)?.isRequired ?? false,

      /** Check if a field is locked */
      isLocked: (key: string) => map.get(key)?.isLocked ?? false,

      /** Get the custom label for a field */
      label: (key: string) => map.get(key)?.label ?? key,

      /** Get all configs sorted by displayOrder */
      all: () => Array.from(map.values()).sort((a, b) => a.displayOrder - b.displayOrder),
    };
  }, [fieldConfigs]);
}
